/**
 * ClearancesTab — manages per-branch clearance types and the per-employee
 * issue/expiration entries. Mounted inside the employee profile view dialog.
 *
 * Permission gate is enforced server-side; UI just talks to the endpoints.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Plus, Pencil, Trash2, AlertTriangle, CheckCircle2, AlertCircle,
  CalendarPlus, Settings2,
} from "lucide-react";

const STATUS_BADGES = {
  valid: { label: "Valid", cls: "bg-[#DCFCE7] text-[#166534]", Icon: CheckCircle2 },
  expiring_soon: { label: "Expiring Soon", cls: "bg-[#FEF3C7] text-[#92400E]", Icon: AlertTriangle },
  expired: { label: "Expired", cls: "bg-[#FEE2E2] text-[#991B1B]", Icon: AlertCircle },
};

export default function ClearancesTab({ employee }) {
  const branchId = employee?.branch_id;
  const employeeId = employee?.id;

  const [types, setTypes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [typeManagerOpen, setTypeManagerOpen] = useState(false);
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState({ clearance_type_id: "", issue_date: "", expiration_date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!branchId || !employeeId) return;
    setLoading(true);
    try {
      const [typesRes, entriesRes] = await Promise.all([
        api.get(`/branches/${branchId}/clearance-types`),
        api.get(`/employees/${employeeId}/clearances`),
      ]);
      setTypes(typesRes.data || []);
      setEntries(entriesRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [branchId, employeeId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ clearance_type_id: types[0]?.id || "", issue_date: "", expiration_date: "", notes: "" });
    setEntryFormOpen(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      clearance_type_id: entry.clearance_type_id,
      issue_date: entry.issue_date,
      expiration_date: entry.expiration_date,
      notes: entry.notes || "",
    });
    setEntryFormOpen(true);
  };

  const submitEntry = async (e) => {
    e.preventDefault();
    if (!form.clearance_type_id) { toast.error("Pick a clearance type"); return; }
    if (!form.issue_date || !form.expiration_date) { toast.error("Fill in both dates"); return; }
    if (form.expiration_date < form.issue_date) { toast.error("Expiration must be on or after issue"); return; }
    setBusy(true);
    try {
      if (editingEntry) {
        await api.patch(`/clearances/${editingEntry.id}`, {
          issue_date: form.issue_date,
          expiration_date: form.expiration_date,
          notes: form.notes,
        });
        toast.success("Clearance updated");
      } else {
        await api.post(`/employees/${employeeId}/clearances`, form);
        toast.success("Clearance added");
      }
      setEntryFormOpen(false);
      setEditingEntry(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/clearances/${confirmDelete.id}`);
      toast.success("Clearance removed");
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const typeNames = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t.name])), [types]);

  return (
    <div className="space-y-4" data-testid="clearances-tab">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#EEF3FF] flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#2563EB]" />
          </div>
          <div>
            <h4 className="font-heading font-bold text-foreground">Clearances</h4>
            <p className="text-xs text-brand-inkMuted">
              Tracked for this employee's branch. We warn you 14 days before any expiration.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTypeManagerOpen(true)}
            className="rounded-full border-brand-purple/30 h-9"
            data-testid="manage-types-btn"
          >
            <Settings2 className="w-3.5 h-3.5 mr-1" /> Manage types
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={openAdd}
            disabled={types.length === 0}
            title={types.length === 0 ? "Add a clearance type first" : undefined}
            className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white h-9"
            data-testid="add-clearance-btn"
          >
            <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Add clearance
          </Button>
        </div>
      </div>

      {/* Entries list */}
      {loading ? (
        <div className="text-sm text-brand-inkMuted py-6 text-center">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-purple/20 p-6 text-center">
          <ShieldCheck className="w-7 h-7 text-brand-purple/40 mx-auto mb-2" />
          <p className="text-sm text-brand-inkMuted">
            {types.length === 0
              ? "No clearance types defined for this branch yet. Click \"Manage types\" to create one (e.g. \"NBI Clearance\")."
              : "No clearance records yet. Click \"Add clearance\" to log one."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-brand-purple/15">
                <th className="py-2 px-2 font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Clearance</th>
                <th className="py-2 px-2 font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Issued</th>
                <th className="py-2 px-2 font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Expires</th>
                <th className="py-2 px-2 font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Status</th>
                <th className="py-2 px-2" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const badge = STATUS_BADGES[e.status] || STATUS_BADGES.valid;
                const Icon = badge.Icon;
                return (
                  <tr key={e.id} className="border-b border-brand-purple/10 hover:bg-brand-purpleLight/30" data-testid={`clearance-row-${e.id}`}>
                    <td className="py-2.5 px-2 font-semibold">
                      {e.clearance_type_name || typeNames[e.clearance_type_id] || "—"}
                      {e.notes && <div className="text-xs font-normal text-brand-inkMuted truncate max-w-[200px]" title={e.notes}>{e.notes}</div>}
                    </td>
                    <td className="py-2.5 px-2 tabular-nums">{e.issue_date}</td>
                    <td className="py-2.5 px-2 tabular-nums">{e.expiration_date}</td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
                        <Icon className="w-3 h-3" /> {badge.label}
                        {e.status !== "expired" && e.days_until_expiration >= 0 && (
                          <span className="opacity-80">· {e.days_until_expiration}d</span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEdit(e)}
                        className="p-1.5 rounded-full text-brand-purpleHover hover:bg-brand-purpleLight"
                        title="Edit"
                        data-testid={`clearance-edit-${e.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(e)}
                        className="p-1.5 rounded-full text-[#B91C1C] hover:bg-[#FEE2E2]"
                        title="Delete"
                        data-testid={`clearance-delete-${e.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={entryFormOpen} onOpenChange={(o) => { setEntryFormOpen(o); if (!o) setEditingEntry(null); }}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="clearance-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-brand-purple" />
              {editingEntry ? "Edit Clearance" : "Add Clearance"}
            </DialogTitle>
            <DialogDescription>
              Pick the clearance type and provide issue + expiration dates.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEntry} className="space-y-4" data-testid="clearance-form">
            <div>
              <label className="text-sm font-semibold">Clearance type</label>
              <select
                required
                value={form.clearance_type_id}
                onChange={(ev) => setForm({ ...form, clearance_type_id: ev.target.value })}
                disabled={!!editingEntry}
                className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 disabled:opacity-60"
                data-testid="clearance-type-select"
              >
                <option value="">Select type…</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {editingEntry && (
                <p className="text-[11px] text-brand-inkMuted mt-1">Type can't be changed after creation — delete and re-add to switch.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold">Issue date</label>
                <Input type="date" required value={form.issue_date}
                  onChange={(ev) => setForm({ ...form, issue_date: ev.target.value })}
                  className="rounded-2xl h-11 mt-1.5 border-brand-purple/20"
                  data-testid="clearance-issue-input"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Expiration date</label>
                <Input type="date" required value={form.expiration_date}
                  onChange={(ev) => setForm({ ...form, expiration_date: ev.target.value })}
                  className="rounded-2xl h-11 mt-1.5 border-brand-purple/20"
                  data-testid="clearance-expiration-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold">Notes (optional)</label>
              <Input value={form.notes}
                onChange={(ev) => setForm({ ...form, notes: ev.target.value })}
                className="rounded-2xl h-11 mt-1.5 border-brand-purple/20"
                placeholder="e.g. yearly renewal, ref #1234"
                data-testid="clearance-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEntryFormOpen(false)} className="rounded-full border-brand-purple/30">Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="clearance-save-btn">
                {busy ? "Saving…" : (editingEntry ? "Save changes" : "Add clearance")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Type manager */}
      <ClearanceTypeManagerDialog
        open={typeManagerOpen}
        onOpenChange={setTypeManagerOpen}
        branchId={branchId}
        types={types}
        onChanged={load}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this clearance record?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the entry. The clearance <em>type</em> stays available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteEntry} className="rounded-full bg-[#C62828] hover:bg-[#B71C1C]" data-testid="clearance-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* -----------------------------------------------------------------------------
 * Branch-wide clearance TYPE management (master list)
 * -------------------------------------------------------------------------- */
function ClearanceTypeManagerDialog({ open, onOpenChange, branchId, types, onChanged }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  const create = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(`/branches/${branchId}/clearance-types`, { name });
      toast.success(`"${name}" added`);
      setNewName("");
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async (t) => {
    const name = editingName.trim();
    if (!name || name === t.name) { setEditingId(null); return; }
    try {
      await api.patch(`/clearance-types/${t.id}`, { name });
      toast.success("Renamed");
      setEditingId(null);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeType = async (t) => {
    if (!window.confirm(`Delete "${t.name}" and every employee record using it for this branch?`)) return;
    try {
      await api.delete(`/clearance-types/${t.id}`);
      toast.success("Removed");
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="type-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-brand-purple" /> Clearance Types
          </DialogTitle>
          <DialogDescription>
            Add the clearance categories this branch tracks (e.g. <em>NBI Clearance</em>, <em>Health Certificate</em>, <em>Barangay Clearance</em>). Used by every employee in this branch.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={create} className="flex gap-2 mb-3" data-testid="type-add-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New clearance type"
            className="rounded-2xl h-10 border-brand-purple/20"
            data-testid="type-add-input"
          />
          <Button type="submit" disabled={busy || !newName.trim()} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="type-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </form>

        {types.length === 0 ? (
          <p className="text-sm text-brand-inkMuted text-center py-4">No types yet — add your first one above.</p>
        ) : (
          <ul className="divide-y divide-brand-purple/10 border border-brand-purple/15 rounded-2xl">
            {types.map((t) => (
              <li key={t.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`type-row-${t.id}`}>
                {editingId === t.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="rounded-xl h-9 flex-1"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); saveRename(t); }
                        if (e.key === "Escape") setEditingId(null);
                      }}
                    />
                    <Button type="button" size="sm" onClick={() => saveRename(t)} className="rounded-full h-8 bg-brand-purple text-white">Save</Button>
                  </>
                ) : (
                  <>
                    <span className="font-semibold flex-1">{t.name}</span>
                    <button type="button" onClick={() => { setEditingId(t.id); setEditingName(t.name); }} className="p-1.5 rounded-full text-brand-purpleHover hover:bg-brand-purpleLight" title="Rename" data-testid={`type-rename-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => removeType(t)} className="p-1.5 rounded-full text-[#B91C1C] hover:bg-[#FEE2E2]" title="Delete" data-testid={`type-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="mt-4">
          <Button type="button" onClick={() => onOpenChange(false)} className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
